import axios from 'axios';
import { type ClassValue,clsx } from 'clsx';
import { isEmpty } from 'radash';
import { useEffect } from 'react';
import { extendTailwindMerge } from 'tailwind-merge';

import { MIME_TYPES } from '@/constants';
import { DataModelAttribute } from '@/types/DataModel';

import { getInjector, getRootScope } from './angularjs-bridge';
import { logger } from './logger';

const twMerge = extendTailwindMerge({ prefix: 'tw-' });

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function restUrl(url: string) {
    //@ts-ignore
    return lax_rest_url(url);
}

/**
 * Encodes a value for safe use in a URL path segment using a custom escape
 * character instead of the standard percent sign. Works like RFC 3986
 * percent-encoding but uses the given {@code escapeCharacter}.
 *
 * For example, with escape character `~`:
 * - `/` (0x2F) -> `~2F`
 * - `~` (0x7E) -> `~7E` (the escape character itself)
 *
 * @param value the raw value to encode
 * @param escapeCharacter the character to use instead of `%`
 */
export function encodePathSegment(value: string, escapeCharacter: string): string {
    if (!value.includes('/') && !value.includes(escapeCharacter)) {
        return value;
    }
    let result = '';
    for (const char of value) {
        if (char === escapeCharacter) {
            result += escapeCharacter + char.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0');
        } else if (char === '/') {
            result += escapeCharacter + '2F';
        } else {
            result += char;
        }
    }
    return result;
}

/**
 * Appends the `pathEscapeCharacter` query parameter to a URL if any of the
 * given raw values required path escape encoding (i.e. contain `/` or
 * the escape character itself).
 *
 * @param url the URL to potentially append to
 * @param escapeCharacter the escape character used in encoding
 * @param rawValues the original (unencoded) values to check
 */
export function appendEscapeParam(url: string, escapeCharacter: string, ...rawValues: string[]): string {
    const needsEscaping = rawValues.some(
        v => v.includes('/') || v.includes(escapeCharacter)
    );
    if (!needsEscaping) {
        return url;
    }
    const param = `pathEscapeCharacter=${encodeURIComponent(escapeCharacter)}`;
    return url + (url.includes('?') ? '&' : '?') + param;
}

/**
 * In some cases (micro-frontends), relative URLs need to be relative to the base URL of
 * which the JS bundle was loaded from.
 */
export function relativeUrlResolver(url: string) {
    //@ts-ignore
    return (window.__BYRD_BASE_URL__ || '') + url;
}

export const fetchItemData = async (primaryKey: string, escapeCharacter = '~') => {
    try {
        const url = appendEscapeParam(
            restUrl(`items/${encodePathSegment(primaryKey, escapeCharacter)}`),
            escapeCharacter, primaryKey
        );
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        logger.error('Failed to fetch item data:', error);
        return null;
    }
};

export function toPixelColumnSize(percentage: number) {
    const contentContainerWidth = window.innerWidth - 500;
    return Math.round(contentContainerWidth * percentage);
}

export function useDebouncedEffect(effect: () => void, delay: number, deps: any[]) {
    useEffect(() => {
        const handler = setTimeout(() => {
            effect();
        }, delay);

        return () => {
            clearTimeout(handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, delay]);
}

export function formatDateToMedium(date: any) {
    // FIXME: AngularJS leave me alone
    const angularJsInjector = getInjector();
    const $rootScope = getRootScope(angularJsInjector);
    const locale = $rootScope.language.replace(/_/g, '-');

    const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    };

    const dateStr = date.toLocaleDateString(locale, options);
    const timeStr = date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });

    return `${dateStr} ${timeStr}`;
};

export function isDrawable(mimeType: string) {
    switch(mimeType) {
        case MIME_TYPES.IMAGE_BMP:
        case MIME_TYPES.IMAGE_GIF:
        case MIME_TYPES.IMAGE_JPEG:
        case MIME_TYPES.IMAGE_PNG:
        case MIME_TYPES.IMAGE_SVG:
        case MIME_TYPES.IMAGE_WEBP:
            return true;
        default:
            return false;
    }
};

export function getFilenameFromUrl(url: string) {
    return decodeURI(url.substring(url.lastIndexOf('/') + 1));
}

//@ts-ignore
export const isMicroFrontend = window.__BYRD_MICRO_FRONTEND__ || false;

export const fetchHistory = async (endpoint: string) => {
    const response = await fetch(restUrl(endpoint));
    if (!response.ok) {
        throw new Error('response not found');
    }
    return await response.json();
};

export const createAssetLink = function (row: any) {
    const assetPath = encodeURI(row.assetPath);
    if (row.assetPath && row.assetType === 'IMPORT') {
        return `${restUrl('gatherings/')}${row.assetId}${assetPath}?download=true`;
    } else if (row.assetPath && row.assetType === 'MESSAGING') {
        return `${restUrl('messaging/messagingassets/')}${row.assetId}${assetPath}?download=true`;
    }
    return undefined;
};

export function filterEmptyParams(
    params: Record<string, any>,
): Record<string, any> {
    // We need to remove empty params i.e. '', as it breaks the endpoints.
    return Object.keys(params).reduce(
        (allParams: Partial<Record<string, any>>, currentParam: string) => {
            const paramValue = params[currentParam];
            if (!isEmpty(paramValue)) {
                allParams[currentParam] = paramValue;
            }
            return allParams;
        },
        [],
    );
}
interface PageDataType {
    data: EntryDataType[];
    cursor?: string;
}
interface EntryDataType {
    id: number;
}
export const updatePages = (
    pages: PageDataType[],
    entry: EntryDataType,
    removeEntry = false,
): PageDataType[] => {
    if (isEmpty(pages)) {
        return [
            {
                data: [entry],
                cursor: undefined,
            },
        ];
    }
    // Try to update a previous entry if it exists, otherwise add to front
    let pageIndex = 0;
    let insertAt = 0;
    let entriesToRemove = 0;
    pages.forEach((page, pIndex) => {
        const entryIndex = page.data.findIndex((entry) => entry.id == entry.id);
        if (entryIndex !== -1) {
            pageIndex = pIndex;
            insertAt = entryIndex;
            entriesToRemove = 1;
            return;
        }
    });
    if (removeEntry) {
        pages[pageIndex].data.splice(insertAt, entriesToRemove);
    } else {
        pages[pageIndex].data.splice(insertAt, entriesToRemove, entry);
    }
    return pages;
};

export const getAttributeDescription = (_attribute: DataModelAttribute, t: (arg0: string) => string) => {
    const key = `attribute.${_attribute.name}.description`;
    const translation = t(key);
    return translation !== key ? translation : undefined;
};

export function areLooselyEqual(obj1: Record<string, any>, obj2: Record<string, any>): boolean {

    function normalizeValue(value: any): any {
        return value === undefined || value === null ? '' : value;
    };
    const allKeys = new Set([
        ...Object.keys(obj1),
        ...Object.keys(obj2),
    ]);

    for (const key of allKeys) {
        const val1 = normalizeValue(obj1[key]);
        const val2 = normalizeValue(obj2[key]);

        if (Array.isArray(val1) && Array.isArray(val2)) {
            if (val1.length !== val2.length || !val1.every((v, i) => v === val2[i])) {
                return false;
            }
        } else if (val1 !== val2) {
            return false;
        }
    }

    return true;
}

